import * as React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { Callout, Intent } from '@blueprintjs/core'
import WithNotFound from '../../../../components/AppError/WithNotFound'
import Errors from '../../../../components/Errors'
import ImageUploader from '../../../../components/images/Uploader'
import Spinner from '../../../../components/Spinner'
import Widget from '../../../../components/Widget'
import { AppState } from '../../../../store'
import Form from './Form'

const CLOSED_STATES = new Set(['SOLD', 'EXPIRED', 'UNPAID', 'DELISTED', 'PURGED'])

interface Props {
  parentSetState: any
  isNewProductListing?: boolean
  validateConcurrent?: boolean
  loading?: boolean
  error: any
  images: any
  deletedImages: any
  product: any
  listingId: any
  form: any
  listingStrategyId: any
  isStockListingOnLoad: boolean
  onListingStrategyChange: () => void
}

// A parent component is responsible for the simple state management of form field values, form submission, etc.
// This is responsible for state management:
// - which is localized to certain fields (such as making API calls to fetch possible values)
// - involving interactions between fields (such as debouncing specifics when the category changes)
// - basically everything that we don't need to pull into the higher level which handles form submission
class ShopgoodwillListingForm extends React.Component<
  Props & RouteComponentProps<{ id?: string; listingId?: string }>
> {
  onImageChange = (images, cb: any = () => {}) => {
    this.props.parentSetState(
      s => ({
        images: images.filter(i => !i.removed),
        deletedImages: s.deletedImages.concat(images.filter(i => i.removed))
      }),
      () => cb(this.props.images)
    )
  }

  onFormChange = (key, value) => {
    this.props.parentSetState(s => ({
      form: {
        ...s.form,
        [key]: value
      }
    }))
  }

  onProductChange = (key, value) => {
    this.props.parentSetState(s => ({
      product: {
        ...s.product,
        [key]: value
      }
    }))
  }

  doOfferAdditionalInfo = () => {
    const { state, bidCount } = this.props.form

    return state === 'LISTED' && bidCount > 0
  }

  modifyDisabled = () => {
    const { state, bidCount } = this.props.form

    return CLOSED_STATES.has(state) || bidCount > 0
  }

  renderWarning = () => {
    const { state, bidCount } = this.props.form
    let msg = null
    if (!this.modifyDisabled()) return null

    if (bidCount > 0) {
      msg = `This item has ${bidCount} bid${bidCount > 1 ? 's' : ''}`
    }

    if (state === 'SOLD') {
      msg = 'This item has been sold'
    }

    if (state === 'DELISTED' || state === 'PURGED') {
      msg = 'This listing is no longer active'
    }

    if (state === 'UNPAID' || state === 'EXPIRED') {
      msg = `This item is ${state.toLowerCase()}`
    }

    return (
      <Callout style={{ marginBottom: 20 }} intent={Intent.WARNING}>
        {msg}, some inputs are disabled. New photos can be added, but they may appear out-of-order on Shopgoodwill.
      </Callout>
    )
  }

  render() {
    if (this.props.loading) {
      return <Spinner />
    }

    // TODO(product_inventory_unit): Improve disabled state when implementing product inventory tracking for all marketplaces and/or lister books
    const quantityInputDisabled =
      !!this.props.product.unitTracking || (this.modifyDisabled() && !this.props.isStockListingOnLoad)

    return (
      <>
        <Errors error={this.props.error} />
        {this.renderWarning()}
        <Widget title="Images">
          <ImageUploader
            images={this.props.images}
            onChange={this.onImageChange}
            lockImages={this.props.form.bidCount > 0}
          />
        </Widget>
        <Widget title="Shopgoodwill">
          <Form
            form={this.props.form}
            onFormChange={this.onFormChange}
            product={this.props.product}
            onProductChange={this.onProductChange}
            isDisabled={this.modifyDisabled()}
            isStockDisabled={quantityInputDisabled}
            isConcurrent={this.props.validateConcurrent}
            doOfferAdditionalInfo={this.doOfferAdditionalInfo()}
            onListingStrategyChange={this.props.onListingStrategyChange}
            listingStrategyId={this.props.listingStrategyId}
            listingStrategyDisabled={!!this.props.listingId || this.props.isNewProductListing}
          />
        </Widget>
      </>
    )
  }
}

export const ShopgoodwillListing = connect((state: AppState) => ({ current: state.current }))(
  WithNotFound(ShopgoodwillListingForm)
)
